import { useCallback, useEffect, useState } from "react";
import { useWorkspace } from "@/context/WorkspaceContext";
import { errorMessage, fullName } from "@/lib/api";

export function useFetch(path, params) {
  const { api } = useWorkspace();
  const key = JSON.stringify(params || {});
  const [state, setState] = useState({ data: null, loading: Boolean(path), error: null });

  const load = useCallback(async () => {
    if (!path) return;
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const { data } = await api.get(path, { params: JSON.parse(key) });
      setState({ data, loading: false, error: null });
    } catch (e) {
      setState({ data: null, loading: false, error: errorMessage(e) });
    }
  }, [api, path, key]);

  useEffect(() => { load(); }, [load]);
  return { ...state, reload: load };
}

const LOOKUP_PATHS = {
  companies: ["/companies", (r) => r.name],
  contacts: ["/contacts", (r) => fullName(r) || r.email],
  opportunities: ["/opportunities", (r) => r.name],
  products: ["/product-profiles", (r) => r.name],
  sites: ["/sites", (r) => r.name],
  members: ["/members", (r) => r.name || r.email, (r) => r.user_id],
  campaigns: ["/research-campaigns", (r) => r.name],
  sources: ["/sources", (r) => r.title || r.url],
  projects: ["/projects", (r) => r.name],
  evidence: ["/evidence", (r) => `${r.verification_status === "verified" ? "✓ " : ""}${r.excerpt.slice(0, 90)}${r.excerpt.length > 90 ? "…" : ""}`],
};

export function useLookups(names) {
  const { api } = useWorkspace();
  const key = names.join(",");
  const [lookups, setLookups] = useState({});
  useEffect(() => {
    let alive = true;
    Promise.all(key.split(",").filter(Boolean).map(async (n) => {
      const [path, label, idOf = (r) => r.id] = LOOKUP_PATHS[n];
      try {
        const { data } = await api.get(path, { params: n === "members" ? {} : { limit: 2000 } });
        return [n, Object.fromEntries(data.filter((r) => idOf(r)).map((r) => [idOf(r), label(r)]))];
      } catch {
        return [n, {}];
      }
    })).then((entries) => alive && setLookups(Object.fromEntries(entries)));
    return () => { alive = false; };
  }, [api, key]);
  return lookups;
}
